"use client";

/**
 * Drawer — right-slide panel for "inspect deeper" evidence on a decision
 * surface. Companion to the existing {@link Modal}: a Modal centers on the
 * viewport and dims the page; a Drawer keeps the decision visible behind a
 * semi-transparent scrim so the operator never loses the question.
 *
 * Rules:
 *
 *   - One drawer at a time. Mounting a second `Drawer` with `open=true`
 *     while another is already open simply replaces the previous content
 *     visually — there is no stacking. (Callers must serialise opens.)
 *   - Closes on Esc and on outside-scrim click.
 *   - On < `sm` viewports the drawer becomes a bottom-sheet full-width so
 *     mobile readers don't have to scroll horizontally inside a 480px
 *     column.
 *   - Focus is left to the consumer; we don't trap it (most drawers
 *     contain read-only evidence). Modals trap focus; drawers do not.
 */
import { useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { IconButton } from "./icon-button";

export function Drawer({
  open, onClose, title, subtitle, children, footer, width = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: "md" | "lg";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widthCls = width === "lg" ? "sm:max-w-2xl" : "sm:max-w-md";

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-stretch sm:justify-end bg-black/40 backdrop-blur-[1px] animate-in fade-in"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <aside
        className={cn(
          "w-full bg-bg-base border-line-subtle overflow-hidden flex flex-col",
          "rounded-t-lg sm:rounded-none sm:rounded-l-lg",
          "border-t sm:border-t-0 sm:border-l",
          "max-h-[85vh] sm:max-h-none sm:h-full",
          widthCls,
          "animate-in slide-in-from-bottom-2 sm:slide-in-from-right-2",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between px-4 py-3 border-b border-line-subtle shrink-0 bg-bg-raised/40">
          <div className="min-w-0">
            <h2 className="text-[13px] font-semibold text-fg-bright truncate">{title}</h2>
            {subtitle && <p className="text-[11.5px] text-fg-muted mt-0.5 truncate">{subtitle}</p>}
          </div>
          <IconButton icon={X} label="Close" onClick={onClose} />
        </header>
        <div className="px-4 py-3 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <footer className="px-4 py-2.5 border-t border-line-subtle bg-bg-base/60 flex items-center justify-end gap-2 shrink-0">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}
